import { ACTION_SEARCH_DATE_RANGE } from 'utils/analytics/eventTypeConstantsAmplitude';
import { AvailabilitySearchQuery } from 'types';
import { CityHeader, cityHeader } from 'routes/www/availability-search/cityHeader';
import {
  DEFAULT_MAX_SEARCH_PARTY_SIZE_DISPLAY,
  OfferingSearchTypeOrBusinessType,
  OfferingTypeOption,
  generateGuestOptions,
  generateOfferingTypeOptions,
  getOfferingTypeString,
  isBusinessType,
} from 'utils/searchBar';
import { DisplayDate, DisplayTime } from '@tock/time';
import { ExperienceTags } from 'components/www/availability-search/filters/SearchFilterExperienceTags';
import { Head } from 'head/Head';
import { LocalDate } from 'js-joda';
import {
  NO_SEARCH_UPDATES_SENTINEL,
  OnSearchType,
} from 'components/www/availability-search/QuerySearchBarButton';
import { SearchBarDesktop } from 'components/www/availability-search/SearchBarDesktop';
import {
  SearchQueryStringParams,
  getCollectionPageDatesFromConfig,
  useIsDateQuickFilterEnabled,
} from 'components/search/utils';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer } from '@tock/coretypes';
import { currentMetroAreaSelector } from 'selectors/metroAreaSelectors';
import { defaultMissingParams } from 'utils/citySearchUtils';
import { getQuickFilterLabel, getQuickFilterType } from 'utils/search/quickFilterUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useCurrentDayRef } from 'hooks/search/useCurrentDayRef';
import { useFlatSearchQuery } from 'hooks/search/useFlatSearchQuery';
import { useHistory, useLocation } from 'react-router';
import { useQueryStringParams } from 'hooks/useQueryStringParams';
import { useRoutedCollectionPageInfo } from 'selectors/consumerPageSelectors';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import { useSelector } from 'react-redux';
import React, { FunctionComponent, ReactNode } from 'react';
import SearchBarMobile from 'components/www/availability-search/SearchBarMobile';
import _ from 'lodash';
import pluralize from 'pluralize';

// Label for null 'party size' / 'num guests' state
export const NULL_PARTY_SIZE_LABEL = 'Party size';

// Search filters fields
export interface SearchFiltersFields {
  cuisines: string[];
  type: OfferingSearchTypeOrBusinessType | undefined;
  priceRange: string | undefined;
  experienceTags: ExperienceTags;
}

export enum SearchFiltersFieldNames {
  type = 'type',
  cuisines = 'cuisines',
  priceRange = 'priceRange',
  experienceTagAll = 'experienceTags.experienceTagAll',
  experienceTagDineIn = 'experienceTags.experienceTagDineIn',
  experienceTagToGo = 'experienceTags.experienceTagToGo',
  experienceTagSeatingArea = 'experienceTags.experienceTagSeatingArea',
  experienceTagCampaign = 'experienceTags.experienceTagCampaign',
}

/**
 * Base props for both search mobile and search desktop headers
 */
export interface SearchChangeProps {
  q?: string | string[];
  date?: string;
  dateEnd?: string;
  time?: string;
  size?: string;
  type?: OfferingSearchTypeOrBusinessType;
  cuisines: string[];
  experienceTags: ExperienceTags;
  priceRange?: string;
  currentQ?: string | string[];
  currentDate?: string;
  currentDateEnd?: string;
  currentTime?: string;
  currentSize?: string;
  currentType?: OfferingSearchTypeOrBusinessType;
  onChangeType(type: OfferingSearchTypeOrBusinessType): void;
  onChangeQ(q: string): void;
  onChangeSize(size: string): void;
  onChangeTime(time: string): void;
  onChangeDate(date: string): void;
  onChangeCuisines(cuisines: string[]): void;
  onChangePriceRange(priceRange: string): void;
  onChangeSearchFilters(fields: SearchFiltersFields): void;
  onClearCollectionTag(): void;
  onSearch: OnSearchType;
  onReset(): void;
  dateLabel: ReactNode;
  timeLabel: ReactNode;
  showTimeFilterLabel: boolean;
  guestsLabel: ReactNode;
  experiencesLabel: ReactNode;
  qLabel: ReactNode;
  collectionTagLabel?: string;
  header?: CityHeader;
  startDate: LocalDate;
  endDate: LocalDate;
  sizeOptions: Option[];
  typeOptions: OfferingTypeOption[];
  onChangeDateRange?(startDate?: LocalDate, endDate?: LocalDate): void;
}

/*
 * In cases where data may be set by flatQueryParameters, or by collection parameters,
 * or by query parameters, look at the query value first, followed by the collection value,
 * then the flatquery value.
 */
const computeDates = (
  queryParams: SearchQueryStringParams,
  {
    collectionDate,
    collectionDateEnd,
  }: {
    collectionDate?: LocalDate;
    collectionDateEnd?: LocalDate;
  },
  flatSearchQuery: AvailabilitySearchQuery
): { date?: string; dateEnd?: string } => {
  if (queryParams.date) {
    // Then the query setting wins, everything else should be ignored
    return queryParams;
  } else {
    // Otherwise, follow the progression:
    return {
      date: collectionDate?.toString() ?? flatSearchQuery.date.toString(),
      dateEnd:
        queryParams.dateEnd ?? collectionDateEnd?.toString() ?? flatSearchQuery.dateEnd?.toString(),
    };
  }
};

/*
 * In cases where type may be set by flatQueryParameters, or by collection parameters,
 * or by query parameters, look at the query value first, followed by the collection value,
 * then the flatquery value.
 */
const computeType = (
  queryParams: SearchQueryStringParams,
  collectionType: consumer.OfferingSearchType | undefined,
  flatSearchQuery: AvailabilitySearchQuery
): OfferingSearchTypeOrBusinessType => {
  const typeFromQueryParams: OfferingSearchTypeOrBusinessType | undefined = isBusinessType(
    queryParams.businessType
  )
    ? 'winery'
    : queryParams.type;

  return typeFromQueryParams ?? collectionType ?? flatSearchQuery.type;
};

/**
 * Search header displays both desktop and mobile headers
 * The temp state is shared between the two bars, as well as basic
 * methods to update, search, etc.
 */
export const SearchHeader: FunctionComponent = () => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const flatSearchQuery = useFlatSearchQuery();
  const searchHandlers = useSearchHandlerContext();

  const queryStringParams: SearchQueryStringParams =
    useQueryStringParams<SearchQueryStringParams>();

  // We use `name` as a proxy for whether there is a collection in play here or not.
  const {
    name: collectionTagLabel,
    dateStart,
    dateEnd,
    metroArea: collectionMetroArea,
    offeringSearchType: collectionOfferingSearchType,
  } = useRoutedCollectionPageInfo();

  const isCollectionPage = !!collectionTagLabel;

  const currentMetroArea = useSelector(currentMetroAreaSelector);
  const today = useCurrentDayRef(currentMetroArea?.timezone);

  const collectionDates = React.useMemo(() => {
    if (!isCollectionPage) {
      return {
        collectionDate: undefined,
        collectionDateEnd: undefined,
      };
    }

    return getCollectionPageDatesFromConfig({ dateStart, dateEnd }, today);
  }, [isCollectionPage, dateStart, dateEnd, today]);

  // The search results bar gets temporary copies of the query to default its forms.
  const [selectedDateInternal, setSelectedDateInternal] = React.useState<string | undefined>(
    computeDates(queryStringParams, collectionDates, flatSearchQuery).date
  );
  const [selectedDateEndInternal, setSelectedDateEndInternal] = React.useState<string | undefined>(
    // Repetitive work to the above, but makes the code more legible.
    computeDates(queryStringParams, collectionDates, flatSearchQuery).dateEnd
  );
  const [selectedTypeInternal, setSelectedTypeInternal] = React.useState<
    OfferingSearchTypeOrBusinessType | undefined
  >(computeType(queryStringParams, collectionOfferingSearchType, flatSearchQuery));
  const [selectedQInternal, setSelectedQInternal] = React.useState<string | string[] | undefined>(
    flatSearchQuery.q ? flatSearchQuery.q : undefined
  );
  const [selectedTimeInternal, setSelectedTimeInternal] = React.useState<string | undefined>(
    flatSearchQuery.time ? flatSearchQuery.time.toString() : undefined
  );
  const [selectedSizeInternal, setSelectedSizeInternal] = React.useState<string | undefined>(
    flatSearchQuery.size ? `${flatSearchQuery.size}` : undefined
  );
  const [selectedCuisinesInternal, setSelectedCuisinesInternal] = React.useState<string[]>(
    flatSearchQuery.cuisines || []
  );
  const [selectedPriceRangeInternal, setSelectedPriceRangeInternal] = React.useState<
    string | undefined
  >(flatSearchQuery.priceRange);
  const [selectedExperienceTagAllInternal, setSelectedExperienceTagAllInternal] = React.useState<
    string[]
  >(flatSearchQuery.experienceTagAll || []);
  const [selectedExperienceTagDineInInternal, setSelectedExperienceTagDineInInternal] =
    React.useState<string[]>(flatSearchQuery.experienceTagDineIn || []);
  const [selectedExperienceTagToGoInternal, setSelectedExperienceTagToGoInternal] = React.useState<
    string[]
  >(flatSearchQuery.experienceTagToGo || []);
  const [selectedExperienceTagCampaignInternal, setSelectedExperienceTagCampaignInternal] =
    React.useState<string[]>(flatSearchQuery.experienceTagCampaign || []);
  const [selectedExperienceTagSeatingAreaInternal, setSelectedExperienceTagSeatingAreaInternal] =
    React.useState<string[]>(flatSearchQuery.experienceTagSeatingArea || []);

  const handleReset = () => {
    const { date: computedDate, dateEnd: computedDateEnd } = computeDates(
      queryStringParams,
      collectionDates,
      flatSearchQuery
    );
    const type = computeType(queryStringParams, collectionOfferingSearchType, flatSearchQuery);

    setSelectedDateInternal(computedDate);
    setSelectedDateEndInternal(computedDateEnd);
    setSelectedTypeInternal(type);
    setSelectedQInternal(flatSearchQuery.q ? flatSearchQuery.q : undefined);
    setSelectedTimeInternal(flatSearchQuery.time ? flatSearchQuery.time.toString() : undefined);
    setSelectedSizeInternal(flatSearchQuery.size ? `${flatSearchQuery.size}` : undefined);
    setSelectedCuisinesInternal(flatSearchQuery.cuisines || []);
    setSelectedPriceRangeInternal(flatSearchQuery.priceRange);
    setSelectedExperienceTagAllInternal(flatSearchQuery.experienceTagAll || []);
    setSelectedExperienceTagDineInInternal(flatSearchQuery.experienceTagDineIn || []);
    setSelectedExperienceTagToGoInternal(flatSearchQuery.experienceTagToGo || []);
    setSelectedExperienceTagCampaignInternal(flatSearchQuery.experienceTagCampaign || []);
    setSelectedExperienceTagSeatingAreaInternal(flatSearchQuery.experienceTagSeatingArea || []);
  };

  /**
   * keep temp state in sync with flatQuery
   */
  React.useEffect(handleReset, [
    flatSearchQuery,
    collectionDates,
    collectionOfferingSearchType,
    queryStringParams,
  ]);

  // If a search dateEnd is present (indicating that we're in a date range), we want to hide the
  // time label in the filter button until a single date is actually selected, regardless of
  // whether or not a default time is already present
  const [showTimeFilterLabel, setShowTimeFilterLabel] = React.useState(true);
  React.useEffect(() => {
    if (selectedDateEndInternal) {
      setShowTimeFilterLabel(false);
    } else {
      setShowTimeFilterLabel(true);
    }
  }, [selectedDateEndInternal]);

  /**
   * can either receive specific params, or will use the current temp state
   */
  const handleSearch: OnSearchType = (params) => {
    if (!selectedDateEndInternal && !showTimeFilterLabel) {
      setShowTimeFilterLabel(true);
    }

    const newParams =
      params === NO_SEARCH_UPDATES_SENTINEL
        ? {
            businessType: isBusinessType(selectedTypeInternal) ? selectedTypeInternal : undefined,
            size: selectedSizeInternal,
            time: selectedTimeInternal,
            q: selectedQInternal,
            dateEnd: selectedDateEndInternal,
            date: selectedDateInternal,
            type: isBusinessType(selectedTypeInternal) ? undefined : selectedTypeInternal,
            cuisines: selectedCuisinesInternal,
            priceRange: selectedPriceRangeInternal,
            experienceTagAll: selectedExperienceTagAllInternal,
            experienceTagDineIn: selectedExperienceTagDineInInternal,
            experienceTagToGo: selectedExperienceTagToGoInternal,
            experienceTagCampaign: selectedExperienceTagCampaignInternal,
            experienceTagSeatingArea: selectedExperienceTagSeatingAreaInternal,
          }
        : defaultMissingParams(flatSearchQuery, params, queryStringParams);

    recordSearchMetrics(newParams);

    searchHandlers?.updateSearchUrl(newParams);
  };

  // handlers used in both mobile and desktop, only values here
  // becaues the events come from different ui elements
  const handleSelectDateInternal = (date: string) => {
    if (selectedDateEndInternal) {
      setSelectedDateEndInternal(undefined);
    }
    setSelectedDateInternal(date);
  };

  const handleSelectDateEndInternal = (date?: string) => {
    setSelectedDateEndInternal(date);
  };

  const onChangeDateRange = (startDate?: LocalDate, endDate?: LocalDate) => {
    const dateStart = startDate?.toString();
    if (dateStart !== selectedDateInternal) {
      handleSelectDateInternal(dateStart ?? LocalDate.now().toString());
    }
    handleSelectDateEndInternal(endDate?.toString() ?? undefined);
  };

  const handleSelectTimeInternal = (time: string) => {
    setSelectedTimeInternal(time);
  };

  const handleSelectSizeInternal = (size: string) => {
    setSelectedSizeInternal(size);
  };

  const handleSelectQInternal = (q: string) => {
    setSelectedQInternal(q);
  };

  const handleSelectTypeInternal = (type: consumer.OfferingSearchType) => {
    if (selectedTypeInternal === 'EVENT') {
      //Reset dateEnd for non-event type since those types dont use date range search
      setSelectedDateEndInternal(undefined);
    }
    setSelectedTypeInternal(type);
  };

  const handleSelectCuisines = (cuisines: string[]) => {
    setSelectedCuisinesInternal(cuisines);
    handleSearch({ cuisines });
  };

  const handleChangePriceRange = (priceRange: string) => {
    setSelectedPriceRangeInternal(priceRange);
    handleSearch({ priceRange });
  };

  const handleChangeSearchFilters = ({
    cuisines,
    type,
    priceRange,
    experienceTags,
  }: SearchFiltersFields) => {
    const {
      experienceTagCampaign,
      experienceTagAll,
      experienceTagDineIn,
      experienceTagSeatingArea,
      experienceTagToGo,
    } = experienceTags;

    setSelectedCuisinesInternal(cuisines);
    setSelectedTypeInternal(type);
    setSelectedPriceRangeInternal(priceRange);
    setSelectedExperienceTagAllInternal(experienceTagAll);
    setSelectedExperienceTagDineInInternal(experienceTagDineIn);
    setSelectedExperienceTagToGoInternal(experienceTagToGo);
    setSelectedExperienceTagCampaignInternal(experienceTagCampaign);
    setSelectedExperienceTagSeatingAreaInternal(experienceTagSeatingArea);

    handleSearch({
      cuisines,
      type: isBusinessType(type) ? undefined : type,
      businessType: isBusinessType(type) ? type : undefined,
      priceRange,
      ...experienceTags,
    });
  };

  const history = useHistory();
  const { search } = useLocation();

  const handleClearCollectionTag = () => {
    const citySlug = collectionMetroArea?.slug ?? currentMetroArea?.slug;

    // The fallback below shouldn't ever really be needed, but we include this
    // just in case.
    const pushTarget = citySlug ? `/city/${citySlug}/search${search}` : '/city/search';

    history.push(pushTarget);
  };

  const getHeader = () => {
    if (searchHandlers?.mode?.stateType) {
      switch (searchHandlers?.mode.stateType) {
        case 'collectionPage':
        case 'city': {
          return cityHeader(true, searchHandlers?.mode, queryStringParams);
        }
        case 'group': {
          return {
            header: searchHandlers?.mode.businessGroup.name,
            shortHeader: searchHandlers?.mode.businessGroup.name,
          };
        }
        case 'togo': {
          const togoHeader = 'Food, cocktails, & wine—to go.';
          return {
            header: togoHeader,
            shortHeader: togoHeader,
          };
        }
        default: {
          assertNever(searchHandlers?.mode);
        }
      }
    }
    return undefined;
  };

  const currentQ = flatSearchQuery.q;
  const currentTime = flatSearchQuery?.time.toString();
  const currentSize = flatSearchQuery?.size ? flatSearchQuery.size?.toString() : undefined;
  const currentType = computeType(queryStringParams, collectionOfferingSearchType, flatSearchQuery);

  const useQuickFilter = useIsDateQuickFilterEnabled(selectedTypeInternal);
  // Anything specified in the query string wins out over internal state. The two will be reconciled
  // shortly anyway.
  const displayDateStart = queryStringParams.date ?? selectedDateInternal;
  //for events get from query string. otherwise dont get from selected date.
  //once the search param is updated, queryStringParams will get the dateEnd.
  const displayDateEnd =
    queryStringParams.dateEnd ?? (useQuickFilter ? undefined : selectedDateEndInternal);

  // shared labels in both ui's
  const computeDateLabel = () => {
    //Attempt to display the event quick filter label if the date or date range match a filter
    //otherwise display the range
    const displayQuickFilterType = getQuickFilterType(
      displayDateStart,
      displayDateEnd ?? displayDateStart
    );
    const displayQuickFilterLabel =
      displayQuickFilterType !== undefined
        ? getQuickFilterLabel(displayQuickFilterType)
        : undefined;
    if (useQuickFilter && displayQuickFilterLabel !== undefined) {
      return <>{displayQuickFilterLabel}</>;
    } else if (displayDateStart && displayDateEnd && displayDateStart !== displayDateEnd) {
      return (
        <>
          <DisplayDate
            dateValue={displayDateStart}
            displayMonthAs="short"
            showWeekday={false}
            showYear={false}
          />
          -
          <DisplayDate
            dateValue={displayDateEnd}
            showMonth={displayDateStart.substr(5, 2) !== displayDateEnd.substr(5, 2)}
            displayMonthAs="short"
            showWeekday={false}
            showYear={false}
          />
        </>
      );
    } else if (displayDateStart) {
      return (
        <DisplayDate
          dateValue={displayDateStart}
          displayMonthAs="short"
          displayWeekdayAs="short"
          showWeekday={false}
          showYear={false}
        />
      );
    } else {
      return 'Date';
    }
  };

  const dateLabel = computeDateLabel();

  const timeLabel = currentTime ? <DisplayTime timeValue={currentTime.toString()} /> : 'Time';

  const guestsLabel = currentSize
    ? `${pluralize('guest', Number(currentSize), true)}`
    : NULL_PARTY_SIZE_LABEL;

  const experiencesLabel = currentType ? getOfferingTypeString(currentType) : 'Experiences';

  const truncatedQ = _.truncate(Array.isArray(currentQ) ? currentQ.join(',') : currentQ, {
    length: 12,
    separator: ' ',
  });

  const header = getHeader();

  const endDate = today.plusYears(1);

  const sizeOptions = generateGuestOptions(1, DEFAULT_MAX_SEARCH_PARTY_SIZE_DISPLAY);

  const offeringTypeOptions = !searchHandlers?.mode
    ? []
    : generateOfferingTypeOptions(searchHandlers.mode);

  const experienceTags = {
    experienceTagAll: selectedExperienceTagAllInternal,
    experienceTagDineIn: selectedExperienceTagDineInInternal,
    experienceTagToGo: selectedExperienceTagToGoInternal,
    experienceTagCampaign: selectedExperienceTagCampaignInternal,
    experienceTagSeatingArea: selectedExperienceTagSeatingAreaInternal,
  };

  const recordSearchMetrics = (params: SearchQueryStringParams) => {
    if (params.type === 'EVENT') {
      if (params.date !== queryStringParams.date) {
        const currentStartDate = params.date ?? queryStringParams.date;
        const currentEndDate = params.dateEnd ?? queryStringParams.dateEnd;
        //for single day search end date is undefined
        const quickFilterType = getQuickFilterType(
          currentStartDate,
          currentEndDate ?? currentStartDate
        );
        const dateRangeLable =
          quickFilterType !== undefined ? getQuickFilterLabel(quickFilterType) : 'Custom';
        //send quick filter label or 'Custom' for date range that doesn't fall within a quick filter option
        amplitudeSendEvent(ACTION_SEARCH_DATE_RANGE, { dateRange: dateRangeLable });
      }
    }
  };

  return (
    <>
      {isCollectionPage ? null : <Head title={header?.header ?? ''} />}
      <SearchBarMobile
        q={selectedQInternal}
        date={selectedDateInternal}
        dateEnd={selectedDateEndInternal}
        time={selectedTimeInternal}
        size={selectedSizeInternal}
        type={selectedTypeInternal}
        collectionTagLabel={collectionTagLabel}
        cuisines={selectedCuisinesInternal}
        priceRange={selectedPriceRangeInternal}
        currentQ={currentQ}
        currentDate={displayDateStart}
        currentDateEnd={displayDateEnd}
        currentTime={currentTime}
        currentSize={currentSize}
        currentType={currentType}
        experienceTags={experienceTags}
        onChangeType={handleSelectTypeInternal}
        onChangeQ={handleSelectQInternal}
        onChangeSize={handleSelectSizeInternal}
        onChangeTime={handleSelectTimeInternal}
        onChangeDate={handleSelectDateInternal}
        onChangeCuisines={handleSelectCuisines}
        onChangePriceRange={handleChangePriceRange}
        onClearCollectionTag={handleClearCollectionTag}
        onChangeSearchFilters={handleChangeSearchFilters}
        onSearch={handleSearch}
        onReset={handleReset}
        dateLabel={dateLabel}
        timeLabel={timeLabel}
        guestsLabel={guestsLabel}
        experiencesLabel={experiencesLabel}
        qLabel={truncatedQ}
        header={header}
        startDate={today}
        endDate={endDate}
        sizeOptions={sizeOptions}
        typeOptions={offeringTypeOptions}
        showTimeFilterLabel={showTimeFilterLabel}
        onChangeDateRange={onChangeDateRange}
      />
      <SearchBarDesktop
        q={selectedQInternal}
        date={selectedDateInternal}
        dateEnd={selectedDateEndInternal}
        time={selectedTimeInternal}
        size={selectedSizeInternal}
        type={selectedTypeInternal}
        collectionTagLabel={collectionTagLabel}
        cuisines={selectedCuisinesInternal}
        experienceTags={experienceTags}
        currentQ={currentQ}
        priceRange={selectedPriceRangeInternal}
        onChangeType={handleSelectTypeInternal}
        onChangeQ={handleSelectQInternal}
        onChangeSize={handleSelectSizeInternal}
        onChangeTime={handleSelectTimeInternal}
        onChangeDate={handleSelectDateInternal}
        onChangeCuisines={handleSelectCuisines}
        onChangePriceRange={handleChangePriceRange}
        onClearCollectionTag={handleClearCollectionTag}
        onChangeSearchFilters={handleChangeSearchFilters}
        onSearch={handleSearch}
        onReset={handleReset}
        dateLabel={dateLabel}
        timeLabel={timeLabel}
        guestsLabel={guestsLabel}
        experiencesLabel={experiencesLabel}
        qLabel={truncatedQ}
        startDate={today}
        endDate={endDate}
        sizeOptions={sizeOptions}
        typeOptions={offeringTypeOptions}
        showTimeFilterLabel={showTimeFilterLabel}
        onChangeDateRange={onChangeDateRange}
      />
    </>
  );
};
